import { useCallback } from "react";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type ITimeRange,
  type ITripTerminus,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { destinationToCity } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { useFilterTime } from "@tenant/flights";
import { Slider } from "@tenant/ui";
import clsx from "clsx";
import dayjs from "dayjs";
import { TIME_RANGE_MAX, TIME_RANGE_STEP } from "../../store";
import defaultStyles from "./FilterTime.module.scss";

export interface FilterTimeComponentProps extends PlatformComponentProps {
  origin?: ITripTerminus;
  destination?: ITripTerminus;
  departureTimeRange: ITimeRange;
  arrivalTimeRange: ITimeRange;
  onSliceTimeRangeChange: (
    departureTimeRange: ITimeRange,
    arrivalTimeRange: ITimeRange
  ) => void;
}

export const FilterTime = ({
  origin,
  destination,
  departureTimeRange,
  arrivalTimeRange,
  onSliceTimeRangeChange,
}: FilterTimeComponentProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.FilterTime;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, formatDateTime, DateTimeFormatStyle } = useI18nContext();

  const {
    departureTimeRangeInternal,
    arrivalTimeRangeInternal,
    handleTimeRangeChange,
    formatDepartureTimeLabel,
    formatArrivalTimeLabel,
  } = useFilterTime({
    initialDepartureTimeRange: departureTimeRange,
    initialArrivalTimeRange: arrivalTimeRange,
    onTimeRangeChange: onSliceTimeRangeChange,
  });

  const getTimeLabel = useCallback(
    (value: number) => {
      const totalTime = dayjs().hour(0).minute(value);
      return formatDateTime(totalTime.toDate(), DateTimeFormatStyle.ShortTime);
    },
    [formatDateTime, DateTimeFormatStyle]
  );

  const departureRangeText = useCallback(
    (timeRange: { min: number; max: number }) => {
      const { min, max } = timeRange;
      if (min === 0 && max === TIME_RANGE_MAX) {
        return t("core-flights.searchFilter.anytime");
      }
      return `${getTimeLabel(min)} - ${getTimeLabel(max)}`;
    },
    [t, getTimeLabel]
  );

  return (
    <div className={clsx(block)}>
      <div className={cn("slice-range")}>
        <div className={cn("slider")}>
          <Slider
            min={0}
            max={TIME_RANGE_MAX}
            ariaLabel=""
            value={[
              departureTimeRangeInternal.min,
              departureTimeRangeInternal.max,
            ]}
            step={TIME_RANGE_STEP}
            onValueChange={(val) =>
              handleTimeRangeChange(
                { min: val[0], max: val[1] },
                arrivalTimeRangeInternal
              )
            }
            minStepsBetweenThumbs={1}
            formatValueLabel={formatDepartureTimeLabel}
          />
        </div>
        <div className={cn("range-subtitle")}>
          {t("core-flights.searchFilter.departFrom", {
            city: destinationToCity(origin),
          })}
        </div>
        <div className={cn("time-range-text")}>
          {departureRangeText(departureTimeRangeInternal)}
        </div>
      </div>
      <div className={cn("slice-range")}>
        <div className={cn("slider")}>
          <Slider
            min={0}
            max={TIME_RANGE_MAX}
            ariaLabel=""
            value={[arrivalTimeRangeInternal.min, arrivalTimeRangeInternal.max]}
            step={TIME_RANGE_STEP}
            onValueChange={(val) =>
              handleTimeRangeChange(departureTimeRangeInternal, {
                min: val[0],
                max: val[1],
              })
            }
            minStepsBetweenThumbs={1}
            formatValueLabel={formatArrivalTimeLabel}
          />
        </div>
        <div className={cn("range-subtitle")}>
          {t("core-flights.searchFilter.arriveIn", {
            city: destinationToCity(destination),
          })}
        </div>
        <div className={cn("time-range-text")}>
          {departureRangeText(arrivalTimeRangeInternal)}
        </div>
      </div>
    </div>
  );
};
